'use client';

/**
 * Hook for fetching customer order statistics from the Magento GraphQL API.
 *
 * Uses the KidsLabs_CustomerOrderStats module that pre-indexes customer
 * order data into a summary table for O(1) lookups.
 *
 * Requires authentication — returns default zeros for guest users.
 *
 * The query is a top-level query (not nested under Customer type) to avoid
 * Apollo Federation composition issues across subgraphs.
 *
 * @see KidsLabs\CustomerOrderStats\Model\Resolver\OrderStats (Magento resolver)
 */

import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { appConfig } from '@kidslabs/module-core';

export const CUSTOMER_ORDER_STATS_QUERY = gql`
    query CustomerOrderStats {
        customerOrderStats {
            completed_order_count
            app_order_count
            web_order_count
            total_order_count
            total_sales_amount
            last_order_date
        }
    }
`;

export interface CustomerOrderStatsData {
    completedOrderCount: number;
    appOrderCount: number;
    webOrderCount: number;
    totalOrderCount: number;
    totalSalesAmount: number;
    lastOrderDate: string | null;
}

const DEFAULT_STATS: CustomerOrderStatsData = {
    completedOrderCount: 0,
    appOrderCount: 0,
    webOrderCount: 0,
    totalOrderCount: 0,
    totalSalesAmount: 0,
    lastOrderDate: null,
};

/**
 * Fetches customer order statistics from the pre-indexed summary table.
 *
 * Performance: Single indexed lookup vs aggregate SQL on sales_order.
 * Cache: Uses cache-and-network to show cached data while refreshing.
 *
 * @param isAuthenticated - Whether the current user is logged in
 * @returns Customer order statistics (defaults to zeros for guests)
 */
export const useCustomerOrderStats = (isAuthenticated: boolean): CustomerOrderStatsData => {
    const { data } = useQuery(CUSTOMER_ORDER_STATS_QUERY, {
        fetchPolicy: 'cache-and-network',
        skip: !isAuthenticated,
        context: {
            uri: appConfig.magentoGraphQlUrl,
        },
    });

    return useMemo<CustomerOrderStatsData>(() => {
        const stats = data?.customerOrderStats;
        if (!stats) {
            return DEFAULT_STATS;
        }

        return {
            completedOrderCount: stats.completed_order_count ?? 0,
            appOrderCount: stats.app_order_count ?? 0,
            webOrderCount: stats.web_order_count ?? 0,
            totalOrderCount: stats.total_order_count ?? 0,
            totalSalesAmount: stats.total_sales_amount ?? 0,
            lastOrderDate: stats.last_order_date ?? null,
        };
    }, [data]);
};
